import type { IconName } from '@/assets/icons/icons'
import type {
  AutomationFlowStepStatisticsInterface,
  AutomationStepType,
  AutomationWaitUntilEventHappensHubspotEventPayload,
  WebhookMethod,
  WebhookTestRequestResponse,
} from '@/data/models/domain/automations/types'
import type { VNode } from '@/composition/vue/compositionApi'
import type { AnyComponent } from '@/components/types'
import type { ButtonColor, StatusColor } from '@/components/shared/types'
import type { StyleColors } from '@/constants/styleColors'
import type { TmWrappers } from '@/wrappers'
import type { AutomationFlowBadgeWithError } from '@/components/domain/automations/types'
import type { FormattedTextNode, TmCountryCode } from '@/services/types'
import type { TmBadgeColors } from '@/components/shared/badge/types'
import type { Satisfy } from '@/types'
import type { RegisteredServices } from '@/core/container/types'
import type { AutomationStepNodeEdgeOffset } from '@/services/domain/automations/types'
import type { Forms } from '@/services/forms/formTypes'

export enum AutomationStepRootCategory {
  TRIGGER = 'trigger',
  ACTION = 'action',
  RULE = 'rule',
  BRANCH = 'branch',
  SYSTEM = 'system',
}

export const automationStepRootCategoryConfigMap = {
  [AutomationStepRootCategory.TRIGGER]: {
    nameKey: 'automations.trigger',
    pluralNameKey: 'automations.triggers',
    icon: 'tmi-flash',
    color: 'red',
  },
  [AutomationStepRootCategory.ACTION]: {
    nameKey: 'automations.action',
    pluralNameKey: 'automations.actions',
    icon: 'tmi-action',
    color: 'green600',
  },
  [AutomationStepRootCategory.RULE]: {
    nameKey: 'automations.rule',
    pluralNameKey: 'automations.rules',
    icon: 'tmi-branch',
    color: 'purple600',
  },
} as const satisfies Record<
  Exclude<AutomationStepRootCategory, AutomationStepRootCategory.SYSTEM | AutomationStepRootCategory.BRANCH>,
  { nameKey: string; pluralNameKey: string; icon: IconName; color: StyleColors }
>

export const automationStepNodeEdgeOffsetMap: Record<AutomationStepNodeEdgeOffset, number> = {
  none: 0,
  small: 5,
  medium: 10,
}

export interface IPhonesInfo {
  label: string
  color: TmBadgeColors | StatusColor
  countryId: TmCountryCode | null
}

export interface IContentFilterInfo {
  title: string
  words: Array<{
    label: string
    color: TmBadgeColors | StatusColor
  }>
  hasValue: boolean
}

export interface ICountryInfo {
  label: string
  countries: Array<{
    label: string
    code: TmCountryCode | null
    color: TmBadgeColors | StatusColor
  }>
}

export interface IInboxInfo {
  label: string
  color: TmBadgeColors | StatusColor
  isDeleted?: boolean
}

export interface IEmailContentFilterInfo {
  title: string
  words: Array<{
    label: string
    color: TmBadgeColors | StatusColor
  }>
  hasValue: boolean
}

// ### AutomationFlowSidebarWrapper ###

export type AutomationFlowSidebarWrapperType = 'settings' | 'steps' | 'step' | 'aiBuilder'

// If the handler returns true, the sidebar will be closed
export type AutomationFlowSidebarWrapperCloseHandler = () => Promise<boolean>

export type AutomationFlowSidebarWrapperOpenTheSameHandler = () => void

type AbstractAutomationFlowSidebarWrapperParams<
  Type extends AutomationFlowSidebarWrapperType,
  Payload extends Record<string, unknown> = Record<string, never>,
> = {
  type: Type
  payload: Payload
  closeHandler?: AutomationFlowSidebarWrapperCloseHandler
  // Sync callback to check if there are unsaved changes
  // used to show a confirmation dialog when the user tries to leave the current route or close the page
  checkUnsavedChangesCallback?: () => boolean
  submitForm?: () => Promise<void>
  // Callbacks to handle when the sidebar is already opened and we try to open it again
  openTheSameHandlers?: AutomationFlowSidebarWrapperOpenTheSameHandler[]
}

export type SettingsWrapperParams = AbstractAutomationFlowSidebarWrapperParams<'settings'>

export type StepsWrapperActionParams =
  | {
      type: 'addStep'
      // 0 - if we are going to add a trigger step (trigger step has no parent)
      // undefined - allow to add a step only via drag and drop (because we don't know the parent step)
      parentStepLocalId?: number
    }
  | {
      type: 'replaceStep'
      stepLocalId: number
    }

export type StepsWrapperParams = AbstractAutomationFlowSidebarWrapperParams<
  'steps',
  {
    actionParams: StepsWrapperActionParams
    availableStepCategories?: AutomationStepRootCategory[]
    selectedStepCategory?: AutomationStepRootCategory
  }
>

export type StepWrapperExtraDataBase = Record<string, unknown>

type StepWrapperExtraDataMap = Satisfy<
  Partial<Record<AutomationStepType, StepWrapperExtraDataBase>>,
  {
    [AutomationStepType.RULE_REPLY_BASED_SPLIT]: {
      showBranchWithIndexOnOpen?: number | 'anyReply' | 'noReply'
      addNewBranchOnOpen?: boolean
    }
    [AutomationStepType.RULE_PERCENTAGE_SPLIT]: {
      showBranchWithIndexOnOpen?: number
    }
  }
>

export type StepWrapperExtraData<StepType extends AutomationStepType = AutomationStepType> =
  StepType extends keyof StepWrapperExtraDataMap ? StepWrapperExtraDataMap[StepType] : never

export type StepWrapperBackToStepsParams = StepsWrapperParams['payload'] & {
  undoRedoSequence: number
}

export type StepWrapperParams<StepType extends AutomationStepType = AutomationStepType> =
  AbstractAutomationFlowSidebarWrapperParams<
    'step',
    {
      stepType: StepType
      stepLocalId: number
      focusOnTitle?: boolean
      showErrorsOnOpen?: boolean
      extraData?: StepWrapperExtraData<StepType>
      backToStepsParams?: StepWrapperBackToStepsParams
    }
  >

export type AiBuilderWrapperParams = AbstractAutomationFlowSidebarWrapperParams<'aiBuilder'>

export type AutomationFlowSidebarWrapperParams =
  | SettingsWrapperParams
  | StepsWrapperParams
  | StepWrapperParams
  | AiBuilderWrapperParams

// ### AutomationFlowConfirmationModal ###

export type AutomationFlowConfirmationModalAction = 'primary' | 'secondary' | 'close'

export type AutomationFlowConfirmationModalParams<
  ActionPayload extends Record<string, unknown> = Record<string, unknown>,
> = {
  title: string
  content:
    | {
        type: 'text'
        text: string
      }
    | {
        type: 'component'
        component: AnyComponent | VNode
        componentProps: Record<string, any>
      }
  primaryButtonText: string
  primaryButtonColor?: ButtonColor
  primaryButtonDisabled?: boolean
  primaryButtonLoadingText?: string
  secondaryButtonText: string
  secondaryButtonColor?: ButtonColor
  secondaryButtonDisabled?: boolean
  secondaryButtonLoadingText?: string
  actionPayload: ActionPayload
  onAction: (action: AutomationFlowConfirmationModalAction, payload: ActionPayload) => void | Promise<void>
}

// ### Step panel ###

export type AutomationStepPanelMenuItem = {
  label: string
  icon: IconName
  isDisabled?: boolean
  tooltip?: string
  onClick?: () => void
}

export type AutomationStepPanelMenu = AutomationStepPanelMenuItem[]

// ### Step dragging ###

export const stepDragImageClassName = 'automation-step-drag-image'

export const vueFlowDisabledDraggingClasses = 'nodrag nopan' // vue-flow classes to prevent dragging and panning by default

interface AutomationDraggingStepBase {
  stepType: AutomationStepType
  dropZoneText: string
  checkIsDropToAreaAllowed?: (area: AutomationFlowStepDropArea) => boolean
}

interface AutomationDraggingNewStep extends AutomationDraggingStepBase {}

interface AutomationDraggingExistingStep extends AutomationDraggingStepBase {
  stepLocalId: number
  parentStepLocalId?: number
}

export type AutomationDraggingStep = AutomationDraggingNewStep | AutomationDraggingExistingStep

export const isAutomationDraggingExistingStep = (
  step: AutomationDraggingStep,
): step is AutomationDraggingExistingStep => {
  return 'stepLocalId' in step
}

export interface AutomationStepDragEvent {
  stepType: AutomationStepType
  event: DragEvent
}

export type AutomationFlowStepDropArea =
  | {
      type: 'placeToAdd'
      parentStepLocalId: number | undefined // undefined for starter node and trigger step, because they have no parent step
    }
  | {
      type: 'step'
      stepLocalId: number
    }

export interface AutomationFlowStepDropAreaParams {
  automationId: string
  availableStepCategories: AutomationStepRootCategory[]
  area: AutomationFlowStepDropArea
}

// ### AutomationFlowDuplicatingModal ###

export type AutomationFlowDuplicatingModalParams = {
  editingId: string
  openFlowAfterDuplicating?: boolean
}

export const automationFlowDuplicatingModalId = 'automationFlowDuplicatingModal' satisfies TmWrappers

// ### AutomationFlowEditingNameModal ###

export type AutomationFlowEditingNameModalParams = {
  editingId: string
}

export const automationFlowEditingNameModalId = 'automationFlowEditingNameModal' satisfies TmWrappers

// ### AutomationFlowActivateWithSettingsModal ###

export type AutomationFlowActivateWithSettingsModalParams = {
  editingId: string
}

export const automationFlowActivateWithSettingsModalId = 'automationFlowActivateWithSettingsModal' satisfies TmWrappers

// ### Pausing or stopping modals ###

export type AutomationFlowPausingOrStoppingSingleModalParams = {
  automationId: string
}

export const automationFlowPausingOrStoppingSingleModalId =
  'automationFlowPausingOrStoppingSingleModal' satisfies TmWrappers

export type AutomationFlowPausingOrStoppingBulkModalParams = {
  tableServiceId: RegisteredServices
}

export const automationFlowPausingOrStoppingBulkModalId =
  'automationFlowPausingOrStoppingBulkModal' satisfies TmWrappers

// ### AutomationStepStatistics ###

export interface AutomationStepComponentStatistics extends Pick<
  AutomationFlowStepStatisticsInterface,
  'successCount' | 'failedCount' | 'waitingCount' | 'skippedCount' | 'exitedCount' | 'jumpedCount'
> {
  totalCount: number
}

// ### Node info ###

interface AutomationNodeInfoBase {
  label: string
}

interface AutomationNodeFormattedNodesInfo extends AutomationNodeInfoBase {
  type: 'formattedNodes'
  nodes: FormattedTextNode[]
}

interface AutomationNodeEmailInfo extends AutomationNodeInfoBase {
  type: 'email'
  subject: string
}

interface AutomationNodeEmailLinksInfo extends AutomationNodeInfoBase {
  type: 'emailLinks'
  automationId: string
  sentEmailActionLocalId: number
  links: string[]
}

interface AutomationNodeContactListInfo extends AutomationNodeInfoBase {
  type: 'contactList'
  lists: Array<(AutomationFlowBadgeWithError & { id: string }) | null>
}

interface AutomationNodeContactTagInfo extends AutomationNodeInfoBase {
  type: 'contactTag'
  tags: Array<AutomationFlowBadgeWithError | null>
}

interface AutomationNodeInboundSmsPhonesInfo extends AutomationNodeInfoBase {
  type: 'inboundSmsPhones'
  phones: IPhonesInfo[]
}

interface AutomationNodeContentInfo extends AutomationNodeInfoBase {
  type: 'content'
  content: IContentFilterInfo
}

interface AutomationNodeCountryInfo extends AutomationNodeInfoBase {
  type: 'country'
  country: ICountryInfo
}

interface AutomationNodeInboundEmailInboxesInfo extends AutomationNodeInfoBase {
  type: 'inboundEmailInboxes'
  inboxes: IInboxInfo[]
}

interface AutomationNodeInboundEmailSubjectInfo extends AutomationNodeInfoBase {
  type: 'inboundEmailSubject'
  words: IEmailContentFilterInfo['words']
}

interface AutomationNodeInboundEmailMessageBodyInfo extends AutomationNodeInfoBase {
  type: 'inboundEmailMessageBody'
  words: IEmailContentFilterInfo['words']
}

export interface AutomationNodeWidgetInfo extends AutomationNodeInfoBase {
  type: 'widget'
  widgetBadges: Array<AutomationFlowBadgeWithError | null>
}

export interface AutomationNodeWhatsappNumberInfo extends AutomationNodeInfoBase {
  type: 'whatsappNumber'
  phoneBadges: Array<{
    id: string
    label: string
  }>
}

export interface AutomationNodeFacebookPageInfo extends AutomationNodeInfoBase {
  type: 'facebookPage'
  pageBadges: Array<AutomationFlowBadgeWithError | null>
}

export interface AutomationNodeInstagramAccountInfo extends AutomationNodeInfoBase {
  type: 'instagramAccount'
  accountBadges: Array<AutomationFlowBadgeWithError | null>
}

// ### AutomationRuleWaitUntilEventHappensNode ###

export interface AutomationNodeShopifyEventInfo {
  type: 'shopifyEvent'
  storeBadge: AutomationFlowBadgeWithError | null
  categoryLabel: string
  categoryBadges: AutomationFlowBadgeWithError[]
  productLabel: string
  productBadges: AutomationFlowBadgeWithError[]
  orderValueLabel: string
  orderValueText: string | null
  orderValueAnyText: string
  isLoading: boolean
}

export interface AutomationNodeWooCommerceEventInfo {
  type: 'wooCommerceEvent'
  storeBadge: AutomationFlowBadgeWithError | null
  categoryLabel: string
  categoryBadges: AutomationFlowBadgeWithError[]
  productLabel: string
  productBadges: AutomationFlowBadgeWithError[]
  orderValueLabel: string
  orderValueText: string | null
  orderValueAnyText: string
  isLoading: boolean
}

export interface AutomationNodeHubspotEventInfo {
  type: 'hubspotEvent'
  payload: AutomationWaitUntilEventHappensHubspotEventPayload
  isPreview: boolean
}

export type AutomationRuleWaitUntilEventHappensNodeInfo =
  | AutomationNodeFormattedNodesInfo
  | AutomationNodeEmailInfo
  | AutomationNodeEmailLinksInfo
  | AutomationNodeContactListInfo
  | AutomationNodeContactTagInfo
  | AutomationNodeInboundSmsPhonesInfo
  | AutomationNodeContentInfo
  | AutomationNodeCountryInfo
  | AutomationNodeInboundEmailInboxesInfo
  | AutomationNodeInboundEmailSubjectInfo
  | AutomationNodeInboundEmailMessageBodyInfo
  | AutomationNodeWidgetInfo
  | AutomationNodeWhatsappNumberInfo
  | AutomationNodeFacebookPageInfo
  | AutomationNodeInstagramAccountInfo
  | AutomationNodeShopifyEventInfo
  | AutomationNodeWooCommerceEventInfo
  | AutomationNodeHubspotEventInfo

export interface AutomationRuleWaitUntilEventHappensInfoGroup {
  label: string
  contentLines: Exclude<
    AutomationRuleWaitUntilEventHappensNodeInfo,
    AutomationNodeShopifyEventInfo | AutomationNodeWooCommerceEventInfo | AutomationNodeHubspotEventInfo
  >[]
}

// ### AutomationRuleReplyBasedSplitBranchNode ###

export type AutomationRuleReplyBasedSplitBranchType = 'userDefined' | 'anyReply' | 'noReply'

export type AutomationRuleReplyBasedSplitBranchNodeInfo =
  | AutomationNodeInboundSmsPhonesInfo
  | AutomationNodeContentInfo
  | AutomationNodeCountryInfo
  | AutomationNodeInboundEmailInboxesInfo
  | AutomationNodeInboundEmailSubjectInfo
  | AutomationNodeInboundEmailMessageBodyInfo

// ### AutomationWebhookTestRequestModal ###

export type AutomationWebhookTestRequestModalParams = {
  method: WebhookMethod
  url: string
  response: WebhookTestRequestResponse
}

export const automationWebhookTestRequestModalId = 'automationWebhookTestRequestModal' satisfies TmWrappers

// ### AutomationWebhookBodyEditorModal ###

export type AutomationWebhookBodyEditorModalParams = {
  formId: Forms
  automationId: string
  stepLocalId: number
}

export const automationWebhookBodyEditorModalId = 'automationWebhookBodyEditorModal' satisfies TmWrappers
